/**
 * RecruitmentBoard — the 2-pane HR recruitment/ATS cockpit.
 *
 * Pattern (matches OnboardingBoard / Front / Linear / Notion):
 *   - LEFT pane: filterable application list (avatar, candidate name, job, state badge).
 *   - RIGHT pane: sticky candidate header + section tabs + section content
 *     (Profile / Job / Review / History).
 *
 * URL contract:
 *   ?id=<application_id> → which application is selected
 *   ?section=<section_key> → which section tab is active in the right pane
 *   ?state=<stage> → hiring-stage filter (omitted means All)
 *   ?department=<department_id|unassigned> → department scope (omitted means All)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Award,
  Briefcase,
  Building2,
  CheckCircle2,
  ChevronRight,
  Clock,
  CreditCard,
  FileText,
  ExternalLink,
  GraduationCap,
  History as HistoryIcon,
  Lightbulb,
  Pause,
  Search,
  User,
  UserCheck,
  Users,
} from 'lucide-react';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import PillSelect from '../../components/UI/PillSelect';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { CountBadge } from '../../components/UI/CountBadge';
import { InfoBadge } from '../../components/UI/InfoBadge';
import {
  DetailBadgeField,
  DetailField,
  DetailFieldGrid,
} from '../../components/UI/DetailFieldGrid';
import RoleBadge from '../../components/RoleBadge';
import ConsentMarkdown from '../../components/Assessments/ConsentMarkdown';
import { formatDateTime } from '../../utils/dateUtils';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  getDepartmentBadgeColor,
  getDepartmentColor,
  getDepartmentIcon,
  getDepartmentShortName,
} from '../../config/departmentConfig';
import {
  isTrackerImportedReview,
  reviewNoteSections,
} from '../../utils/reviewNotes';
import {
  useApplicationsList,
  useApplicationDetail,
} from '../../hooks/useRecruitmentData';

// ============================================================================
// CONSTANTS
// ============================================================================

const SECTIONS = [
  { key: 'profile', label: 'Profile', icon: User },
  { key: 'job', label: 'Job', icon: Briefcase },
  { key: 'review', label: 'Review', icon: FileText },
  { key: 'history', label: 'History', icon: HistoryIcon },
];

// Application state filter options
const STATE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'screening', label: 'Screening' },
  { value: 'kiv', label: 'Keep in view' },
  { value: 'interview_scheduled', label: 'Interview scheduled' },
  { value: 'interview_completed', label: 'Interview completed' },
  { value: 'offer_pending', label: 'Offer pending' },
  { value: 'offer_sent', label: 'Offer sent' },
  { value: 'offer_accepted', label: 'Offer accepted' },
  { value: 'offer_rejected', label: 'Offer rejected' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Park reason filter options
const PARK_REASON_FILTERS = [
  { value: 'all', label: 'All Hold/KIV' },
  { value: 'missing_must_have', label: 'Missing Must-Have' },
  { value: 'timing', label: 'Timing' },
  { value: 'rematch', label: 'Rematch' },
  { value: 'package', label: 'Package' },
];

const isKnownState = (value) => STATE_FILTERS.some((filter) => filter.value === value);

const UNASSIGNED_DEPARTMENT = 'unassigned';

const departmentKey = (application) => application.department_id || UNASSIGNED_DEPARTMENT;

const departmentLabel = (application) => application.department_name || 'Unassigned';

const matchesDepartmentFilter = (application, departmentFilter) => {
  if (!departmentFilter || departmentFilter === 'all') return true;
  if (departmentFilter === UNASSIGNED_DEPARTMENT) {
    return !application.department_id;
  }
  return application.department_id === departmentFilter;
};

const candidateKey = (application) => {
  if (!application) return null;
  if (application.candidate != null && typeof application.candidate === 'object') {
    return application.candidate.id ?? null;
  }
  return application.candidate ?? null;
};

const siblingApplications = (applications, candidateId, excludeId) => {
  if (candidateId == null) return [];
  return applications.filter((app) => (
    app.id !== excludeId && String(candidateKey(app)) === String(candidateId)
  ));
};

const alsoConsideringLabel = (others) => {
  if (!others.length) return '';
  if (others.length === 1) {
    const other = others[0];
    const jobTitle = other.job_title || other.job?.job_title || 'another role';
    const stage = other.current_state_display || '';
    return stage ? `Also ${jobTitle} · ${stage}` : `Also ${jobTitle}`;
  }
  return `Also ${others.length} other roles`;
};

const displayPersonalEmail = (value) => {
  if (value == null || value === '') return '';
  if (typeof value !== 'string') return '';
  if (value.includes('<memory at 0x')) return '';
  return value;
};

const otherRoleCountLabel = (count) => (
  count === 1 ? '2 roles' : `${count + 1} roles`
);

const departmentConfigName = (name) => (name && name !== 'Unassigned' ? name : '');

const DepartmentIdentityMark = ({ name, size = 'sm' }) => {
  const resolved = departmentConfigName(name);
  const DeptIcon = getDepartmentIcon(resolved);
  const colors = getDepartmentColor(resolved);
  const box = size === 'md' ? 'w-8 h-8' : 'w-7 h-7';
  const icon = size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';

  return (
    <div
      className={`${box} rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0`}
      aria-hidden="true"
    >
      <DeptIcon className={`${icon} ${colors.text}`} />
    </div>
  );
};

const DepartmentGroupHeader = ({ name, count }) => {
  const colors = getDepartmentColor(departmentConfigName(name));

  return (
    <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
      <div className="flex items-center gap-2 px-4 py-2">
        <span
          className={`w-1 h-5 rounded-full ${colors.solid} flex-shrink-0`}
          aria-hidden="true"
        />
        <DepartmentIdentityMark name={name} />
        <span className="text-sm font-semibold text-gray-900 truncate" title={name}>
          {name}
        </span>
        <CountBadge count={count} theme="admin" max={9999} />
      </div>
    </div>
  );
};

// Scorecard is a verdict (Pass / Borderline / Reject), not a pipeline stage.
// InfoBadge — not StatusBadge — because this is not a workflow status.
const SCORECARD = {
  hire: { label: 'Pass', color: 'green' },
  kiv: { label: 'Borderline', color: 'amber' },
  reject: { label: 'Reject', color: 'red' },
};

const SCORE_ECHOES_STAGE = {
  reject: new Set(['rejected', 'cancelled']),
  hire: new Set(['hired', 'offer_accepted']),
  kiv: new Set(['kiv']),
};

const scoreEchoesStage = (recommendation, stage) => {
  const echoed = SCORE_ECHOES_STAGE[recommendation];
  return Boolean(echoed && echoed.has(stage));
};

const ScorecardBadge = ({ recommendation, recommendationDisplay }) => {
  const score = SCORECARD[recommendation];
  const label = score?.label || recommendationDisplay || recommendation || '—';
  return (
    <InfoBadge
      color={score?.color || 'gray'}
      label={label}
      title={`Scorecard: ${label}`}
    />
  );
};

// Next action suggestions by state
const NEXT_ACTIONS = {
  new: 'Screen resume and initial fit assessment',
  screening: 'Review candidate profile and schedule interview if promising',
  kiv: 'Re-evaluate when capacity allows or candidate follows up',
  interview_scheduled: 'Prepare interview questions and attend scheduled slot',
  interview_completed: 'Submit hiring manager review and recommendation',
  offer_pending: 'Prepare offer letter with approved compensation',
  offer_sent: 'Await candidate response (follow up if overdue)',
  offer_accepted: 'Collect pre-employment data and schedule start date',
  hired: 'Candidate converted to employee — no further action',
  rejected: 'Archive per PDPA 1-year retention policy',
  cancelled: 'Application withdrawn — no further action',
};

// ============================================================================
// HELPERS
// ============================================================================

const initials = (name) => {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('') || '?';
};

// ============================================================================
// APPLICATION LIST ITEM (LEFT PANE)
// ============================================================================

const ApplicationListItem = ({
  application,
  isSelected,
  onSelect,
  showStageBadge,
  otherApplications = [],
}) => {
  const name = application.candidate_name || '(no name)';
  const jobTitle = application.job_title || '—';
  const stage = application.current_state_value ?? application.current_state_obj?.state ?? 'new';
  const latestReview = application.latest_review;
  const recommendation = latestReview?.recommendation;
  const showScore = Boolean(recommendation) && !scoreEchoesStage(recommendation, stage);
  const scoreBadge = showScore ? (
    <ScorecardBadge
      recommendation={recommendation}
      recommendationDisplay={latestReview.recommendation_display}
    />
  ) : null;
  
  const parkReason = application.park_reason;
  const parkReasonDisplay = application.park_reason_display || parkReason;
  const parkReasonNote = application.park_reason_note;
  const showParkChip = stage === 'kiv' && Boolean(parkReason);
  const parkChipTitle = parkReasonNote 
    ? `Hold/KIV: ${parkReasonDisplay}\n${parkReasonNote}`
    : `Hold/KIV: ${parkReasonDisplay}`;
  
  const alsoLabel = alsoConsideringLabel(otherApplications);
  const accessibleName = alsoLabel
    ? `${name}, ${jobTitle}, ${alsoLabel}`
    : `${name}, ${jobTitle}`;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={accessibleName}
      aria-current={isSelected ? 'true' : undefined}
      className={`w-full text-left p-4 hover:bg-red-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-inset ${
        isSelected ? 'bg-red-50' : ''
      }`}
    >
      <div className="flex items-start gap-3 min-w-0">
        <div
          className="w-10 h-10 rounded-full bg-red-100 text-red-800 flex items-center justify-center text-sm font-semibold flex-shrink-0"
          aria-hidden="true"
        >
          {initials(name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate" title={name}>
            {name}
          </p>
          <p className="text-xs text-gray-600 truncate mt-0.5" title={jobTitle}>
            {jobTitle}
          </p>
          {alsoLabel ? (
            <p className="text-xs text-gray-500 truncate mt-0.5" title={alsoLabel}>
              {alsoLabel}
            </p>
          ) : null}
          {(showStageBadge || scoreBadge || showParkChip || otherApplications.length > 0) && (
            <div className="flex items-center flex-wrap gap-1 mt-1.5">
              {otherApplications.length > 0 ? (
                <InfoBadge
                  color="slate"
                  label={otherRoleCountLabel(otherApplications.length)}
                  title={alsoLabel}
                />
              ) : null}
              {showStageBadge ? <StatusBadge status={stage} /> : null}
              {scoreBadge}
              {showParkChip ? (
                <InfoBadge
                  color="amber"
                  label={parkReasonDisplay}
                  title={parkChipTitle}
                />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

// ============================================================================
// CANDIDATE HEADER (sticky top of right pane)
// ============================================================================

const CandidateHeader = ({ application, otherApplications = [] }) => {
  if (!application) return null;
  
  const name = application.candidate?.full_name || '(no name)';
  const email = displayPersonalEmail(application.candidate?.personal_email) || '—';
  const jobTitle = application.job?.job_title || '—';
  const alsoLabel = alsoConsideringLabel(otherApplications);
  
  return (
    <div className="border-b bg-white px-4 sm:px-6 py-4 flex-shrink-0">
      <div className="flex items-start justify-between gap-3 sm:gap-4 flex-wrap">
        <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
          <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-red-100 text-red-800 flex items-center justify-center text-base sm:text-lg font-bold flex-shrink-0">
            {initials(name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-base sm:text-2xl font-bold text-gray-900 truncate min-w-0">
                {name}
              </h2>
              <div className="flex-shrink-0 pt-0.5">
                <StatusBadge status={application.current_state_value ?? application.current_state_obj?.state ?? 'new'} />
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 mt-1 flex-wrap">
              <span className="truncate">{email}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-2 flex-wrap min-w-0">
              <span className="flex items-center gap-1 min-w-0">
                <Briefcase className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{jobTitle}</span>
              </span>
              {otherApplications.length > 0 && (
                <InfoBadge
                  color="slate"
                  label={otherRoleCountLabel(otherApplications.length)}
                  title={alsoLabel}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SECTION TABS (horizontal, sticky under header)
// ============================================================================

const SectionTabs = ({ sections, active, onChange }) => (
  <div className="border-b bg-white px-4 py-3 flex-shrink-0">
    <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
      {sections.map((s) => {
        const Icon = s.icon;
        const isActive = active === s.key;
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onChange(s.key)}
            className={`tab-btn-admin ${isActive ? 'active' : ''}`}
          >
            <Icon className="w-4 h-4" />
            {s.label}
          </button>
        );
      })}
    </div>
  </div>
);

// ============================================================================
// SECTION COMPONENTS (one per tab)
// ============================================================================

const SectionCard = ({ title, subtitle, children }) => (
  <section className="bg-white rounded-2xl border shadow-sm p-4 sm:p-6">
    <div className="flex flex-col gap-2 mb-4">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900">{title}</h3>
      {subtitle && <p className="text-xs sm:text-sm text-gray-500">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const workHistoryText = (value) => {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  return '';
};

const formatWorkHistoryDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const trimmed = dateStr.trim();
  if (!trimmed) return '';
  
  const monthAbbreviations = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Check if it's a YYYY-MM-DD format (full date)
  const fullDateMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (fullDateMatch) {
    const [, year, month, day] = fullDateMatch;
    const monthIndex = parseInt(month) - 1;
    if (monthIndex >= 0 && monthIndex < 12) {
      const paddedDay = day.padStart(2, '0');
      return `${paddedDay} ${monthAbbreviations[monthIndex]} ${year}`;
    }
  }
  
  // Check if it's a YYYY-MM format (month only)
  const monthOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})$/);
  if (monthOnlyMatch) {
    const [, year, month] = monthOnlyMatch;
    const monthIndex = parseInt(month) - 1;
    if (monthIndex >= 0 && monthIndex < 12) {
      return `${monthAbbreviations[monthIndex]} ${year}`;
    }
  }
  
  // Check MM/YYYY, M/YYYY formats
  const slashFormatMatch = trimmed.match(/^(\d{1,2})\/(\d{4})$/);
  if (slashFormatMatch) {
    const [, month, year] = slashFormatMatch;
    const monthIndex = parseInt(month) - 1;
    if (monthIndex >= 0 && monthIndex < 12) {
      return `${monthAbbreviations[monthIndex]} ${year}`;
    }
  }
  
  // Check MM-YYYY format
  const dashFormatMatch = trimmed.match(/^(\d{1,2})-(\d{4})$/);
  if (dashFormatMatch) {
    const [, month, year] = dashFormatMatch;
    const monthIndex = parseInt(month) - 1;
    if (monthIndex >= 0 && monthIndex < 12) {
      return `${monthAbbreviations[monthIndex]} ${year}`;
    }
  }
  
  // Check for already-localized month names (Jan 2021, January 2021, etc.)
  // Abbreviated months (Jan, Feb, etc.)
  for (let i = 0; i < monthAbbreviations.length; i++) {
    const abbr = monthAbbreviations[i];
    const abbrPattern = new RegExp(`^${abbr}\\s+(\\d{4})$`, 'i');
    const abbrMatch = trimmed.match(abbrPattern);
    if (abbrMatch) {
      return `${abbr} ${abbrMatch[1]}`;
    }
  }
  
  // Full month names (January, February, etc.)
  for (let i = 0; i < monthNames.length; i++) {
    const fullName = monthNames[i];
    const fullPattern = new RegExp(`^${fullName}\\s+(\\d{4})$`, 'i');
    const fullMatch = trimmed.match(fullPattern);
    if (fullMatch) {
      return `${monthAbbreviations[i]} ${fullMatch[1]}`;
    }
  }
  
  // Normalize "Current" to "Present"
  if (trimmed.toLowerCase() === 'current') {
    return 'Present';
  }
  
  // Fallback: return as-is if not in expected format
  return trimmed;
};

const workHistoryPeriod = (start, end) => {
  const startLabel = formatWorkHistoryDate(workHistoryText(start));
  const endLabel = formatWorkHistoryDate(workHistoryText(end));

  if (!startLabel && !endLabel) return '';
  if (!startLabel) return endLabel;
  return `${startLabel} – ${endLabel || 'Present'}`;
};

const EducationSection = ({ extractedData }) => {
  const education = extractedData?.education;
  const educationStatus = extractedData?.education_status;
  const legacyText = extractedData?.education_text_legacy;

  const hasStructuredData = Array.isArray(education) && education.length > 0;
  const hasLegacyData = legacyText && typeof legacyText === 'string' && legacyText.trim();
  const isNotStated = educationStatus === 'not_stated';

  const renderEmptyState = () => {
    if (isNotStated) {
      return (
        <div className="text-center py-12">
          <GraduationCap className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            Education not stated
          </h4>
          <p className="text-sm text-gray-500">
            Candidate did not provide education details in their application.
          </p>
        </div>
      );
    }

    return (
      <div className="text-center py-12">
        <GraduationCap className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h4 className="text-lg font-semibold text-gray-900 mb-2">
          No education captured
        </h4>
        <p className="text-sm text-gray-500">
          Review the candidate's resume for qualifications not yet captured.
        </p>
      </div>
    );
  };

  const renderLegacyFallback = () => (
    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-amber-900 mb-1">
            Legacy education data
          </p>
          <p className="text-sm text-amber-800 whitespace-pre-wrap">
            {legacyText}
          </p>
        </div>
      </div>
    </div>
  );

  const formatYearLabel = (entry) => {
    const { year_completed, status } = entry;
    
    if (status === 'ongoing') {
      return 'Ongoing';
    }
    
    if (status === 'not_stated' || !year_completed) {
      return null;
    }
    
    return year_completed;
  };

  return (
    <SectionCard
      title="Education"
      subtitle="Qualifications captured from the candidate's CV"
    >
      {!hasStructuredData && !hasLegacyData ? (
        renderEmptyState()
      ) : !hasStructuredData && hasLegacyData ? (
        renderLegacyFallback()
      ) : (
        <ol className="divide-y divide-gray-100 rounded-xl border border-gray-200 overflow-hidden">
          {education.map((entry, index) => {
            const qualification = entry.qualification || '(Qualification not specified)';
            const institution = entry.institution;
            const yearLabel = formatYearLabel(entry);

            return (
              <li
                key={`${qualification}-${institution || 'no-inst'}-${entry.year_completed || index}`}
                className="p-3.5"
              >
                <div className="flex items-start gap-2.5">
                  <div
                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0"
                    aria-hidden="true"
                  >
                    <GraduationCap className="w-3.5 h-3.5 text-gray-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between md:gap-3">
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold text-gray-900 leading-snug">
                          {qualification}
                        </h4>
                        {institution ? (
                          <p className="text-sm text-gray-600 mt-0.5 leading-snug">
                            {institution}
                          </p>
                        ) : null}
                      </div>
                      {yearLabel ? (
                        <p className="text-xs text-gray-500 mt-1 md:mt-0 md:whitespace-nowrap md:flex-shrink-0">
                          Year completed: {yearLabel}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
};

const WorkHistorySection = ({ workHistory }) => {
  const entries = Array.isArray(workHistory)
    ? workHistory.filter((entry) => entry && typeof entry === 'object')
    : [];

  const splitNotes = (notes) => {
    if (!notes) return [];
    
    // Split on semicolons and newlines, then clean up
    const parts = notes
      .split(/[;\n]+/)
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    
    return parts;
  };

  return (
    <SectionCard
      title="Work experience"
      subtitle="Employment history captured from the candidate's CV"
    >
      {entries.length === 0 ? (
        <div className="text-center py-12">
          <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            No work history captured
          </h4>
          <p className="text-sm text-gray-500">
            Review the candidate's resume for experience not yet captured.
          </p>
        </div>
      ) : (
        <ol className="divide-y divide-gray-100 sm:rounded-xl sm:border sm:border-gray-200 overflow-hidden">
          {entries.map((entry, index) => {
            const title = workHistoryText(entry.title) || 'Role not specified';
            const employer = workHistoryText(entry.employer);
            const period = workHistoryPeriod(entry.start, entry.end);
            const notes = workHistoryText(entry.notes);
            const noteParts = splitNotes(notes);

            return (
              <li
                key={`${title}-${employer}-${entry.start || ''}-${index}`}
                className="py-3 sm:p-4"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0"
                    aria-hidden="true"
                  >
                    <Briefcase className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {title}
                        </h4>
                        {employer ? (
                          <p className="text-sm text-gray-600 mt-0.5">{employer}</p>
                        ) : null}
                      </div>
                      {period ? (
                        <p className="text-xs font-medium text-gray-500 whitespace-nowrap">
                          {period}
                        </p>
                      ) : null}
                    </div>
                    {noteParts.length > 0 ? (
                      <ul className="mt-2.5 space-y-0.5 list-disc list-outside pl-5">
                        {noteParts.map((part, idx) => (
                          <li key={idx} className="text-sm text-gray-700 leading-tight">
                            {part}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
};

const SalarySection = ({ extractedData }) => {
  const salary = extractedData?.salary;
  const salaryStatus = extractedData?.salary_status;

  const hasUsableFields = salary && typeof salary === 'object' && (salary.last_drawn || salary.expected);

  // Education-style explicit branching
  if (salaryStatus === 'not_stated') {
    // Explicit not_stated: candidate chose not to provide
    return (
      <SectionCard
        title="Salary"
        subtitle="Last drawn and expected compensation"
      >
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
          <p className="text-sm text-gray-500">
            Salary not stated — most candidates do not provide salary details in their CV.
          </p>
        </div>
      </SectionCard>
    );
  } else if (
    salaryStatus === 'empty' ||
    !salaryStatus ||
    (salaryStatus === 'structured' && !hasUsableFields) ||
    (salaryStatus !== 'structured')
  ) {
    // Empty / missing / malformed / structured-without-fields / unknown
    // NEVER say "Salary not stated" for these
    return (
      <SectionCard
        title="Salary"
        subtitle="Last drawn and expected compensation"
      >
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-sm text-amber-800">
            No usable salary data — review the candidate's resume for compensation details.
          </p>
        </div>
      </SectionCard>
    );
  }

  // Happy path: structured with usable fields
  const formatSalary = (value, currency, period) => {
    if (value == null || value === '') return null;
    const curr = currency || 'SGD';
    const per = period ? ` per ${period}` : '';
    if (typeof value === 'number' && Number.isFinite(value)) {
      return `${curr} ${value.toLocaleString()}${per}`;
    }
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const numeric = trimmed.replace(/,/g, '');
    if (/^-?\d+(\.\d+)?$/.test(numeric)) {
      return `${curr} ${parseFloat(numeric).toLocaleString()}${per}`;
    }
    return trimmed;
  };

  return (
    <SectionCard
      title="Salary"
      subtitle="Last drawn and expected compensation"
    >
      <DetailFieldGrid>
        {salary.last_drawn ? (
          <DetailField
            label="Last Drawn"
            value={formatSalary(salary.last_drawn, salary.currency, salary.period)}
            className="col-span-2 sm:col-span-1"
          />
        ) : null}
        {salary.expected ? (
          <DetailField
            label="Expected"
            value={formatSalary(salary.expected, salary.currency, salary.period)}
            className="col-span-2 sm:col-span-1"
          />
        ) : null}
        {salary.source ? (
          <DetailField
            label="Source"
            value={salary.source}
            span="full"
          />
        ) : null}
      </DetailFieldGrid>
    </SectionCard>
  );
};

const CertificationsSection = ({ extractedData }) => {
  const certifications = extractedData?.certifications;

  const hasCertifications = Array.isArray(certifications) && certifications.length > 0;

  const parseCertification = (cert) => {
    if (typeof cert === 'string') {
      const parts = cert.split('|').map((p) => p.trim());
      if (parts.length >= 2) {
        return {
          name: parts[0],
          issuer: parts[1] || null,
          year: parts[2] || null,
          status: null,
        };
      }
      return { name: cert, issuer: null, year: null, status: null };
    }
    
    return {
      name: cert.name || cert.certification || null,
      issuer: cert.issuer || null,
      year: cert.year_completed || cert.year || null,
      status: cert.status || null,
    };
  };

  if (!hasCertifications) {
    return (
      <SectionCard
        title="Certifications"
        subtitle="Professional certifications and licenses"
      >
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
          <p className="text-sm text-gray-500">
            No certifications captured — review the candidate's resume for professional certifications.
          </p>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Certifications"
      subtitle="Professional certifications and licenses"
    >
      <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 overflow-hidden">
        {certifications.map((cert, index) => {
          const parsed = parseCertification(cert);
          const name = parsed.name || '(Certification name not specified)';
          const issuer = parsed.issuer;
          const year = parsed.year;
          const status = parsed.status;
          const isOngoing = status === 'ongoing' || status === 'in_progress';
          
          const yearLabel = isOngoing ? 'Currently pursuing' : year;

          return (
            <li
              key={`${name}-${index}`}
              className="p-3.5"
            >
              <div className="flex items-start gap-2.5">
                <div
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0"
                  aria-hidden="true"
                >
                  <Award className="w-3.5 h-3.5 text-gray-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between md:gap-3">
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 leading-snug">
                        {name}
                      </h4>
                      {issuer ? (
                        <p className="text-sm text-gray-600 mt-0.5 leading-snug">
                          {issuer}
                        </p>
                      ) : null}
                    </div>
                    {yearLabel ? (
                      <p className="text-xs text-gray-500 mt-1 md:mt-0 md:whitespace-nowrap md:flex-shrink-0">
                        {isOngoing ? yearLabel : `Year: ${yearLabel}`}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
};

const WorkPassSection = ({ extractedData }) => {
  const workPass = extractedData?.work_pass;
  const workPassStatus = extractedData?.work_pass_status;

  const isStated = workPassStatus === 'stated' && workPass;
  const isNotStated = workPassStatus === 'not_stated' || !workPass;

  return (
    <SectionCard
      title="Work Pass"
      subtitle="Right to work in Singapore"
    >
      {isNotStated ? (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
          <p className="text-sm text-gray-500">
            Work pass not stated — most CVs do not include work pass information.
          </p>
        </div>
      ) : (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
          <DetailFieldGrid>
            <DetailField
              label="Status"
              value={typeof workPass === 'string' ? workPass : workPass.status || '—'}
            />
            {typeof workPass === 'object' && workPass.type ? (
              <DetailField
                label="Type"
                value={workPass.type}
              />
            ) : null}
            {typeof workPass === 'object' && workPass.expiry ? (
              <DetailField
                label="Expiry"
                value={workPass.expiry}
              />
            ) : null}
          </DetailFieldGrid>
        </div>
      )}
    </SectionCard>
  );
};

const noticeText = (value) => {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
};

const NoticeAvailabilitySection = ({ extractedData }) => {
  const noticeAvailability = extractedData?.notice_availability;
  const noticeStatus = extractedData?.notice_status;

  const empty = (
    <SectionCard
      title="Notice & Availability"
      subtitle="Candidate's notice period and start date"
    >
      <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
        <p className="text-sm text-gray-500">
          Notice and availability not stated.
        </p>
      </div>
    </SectionCard>
  );

  if (
    noticeStatus === 'not_stated'
    || noticeAvailability == null
    || noticeAvailability === ''
    || noticeAvailability === 'not_stated'
  ) {
    return empty;
  }

  if (typeof noticeAvailability === 'string') {
    return (
      <SectionCard
        title="Notice & Availability"
        subtitle="Candidate's notice period and start date"
      >
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
          <p className="text-sm text-gray-900 whitespace-pre-wrap">{noticeAvailability}</p>
        </div>
      </SectionCard>
    );
  }

  if (typeof noticeAvailability !== 'object') {
    return empty;
  }

  const noticePeriod = noticeText(noticeAvailability.notice_period);
  const availableFrom = noticeText(noticeAvailability.available_from);
  const raw = noticeText(noticeAvailability.raw);

  if (!noticePeriod && !availableFrom && !raw) {
    return empty;
  }

  return (
    <SectionCard
      title="Notice & Availability"
      subtitle="Candidate's notice period and start date"
    >
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
        {(noticePeriod || availableFrom) ? (
          <DetailFieldGrid>
            {noticePeriod ? (
              <DetailField label="Notice period" value={noticePeriod} />
            ) : null}
            {availableFrom ? (
              <DetailField label="Available from" value={availableFrom} />
            ) : null}
          </DetailFieldGrid>
        ) : (
          <p className="text-sm text-gray-900 whitespace-pre-wrap">{raw}</p>
        )}
      </div>
    </SectionCard>
  );
};

const SkillsToolsSection = ({ extractedData }) => {
  const skillsTools = extractedData?.skills_tools;
  const skillsToolsStatus = extractedData?.skills_tools_status;

  // Option A coercion: string → string, object → canonical || raw
  const coercedSkills = Array.isArray(skillsTools)
    ? skillsTools
        .map((skill) => {
          if (typeof skill === 'string') return skill.trim();
          if (skill && typeof skill === 'object') {
            return (skill.canonical || skill.raw || '').trim();
          }
          return '';
        })
        .filter((name) => name.length > 0)
    : [];

  // Defensive dedupe: one chip per normalized label (trim + casefold + strip trailing parenthetical)
  const seen = new Set();
  const dedupedSkills = coercedSkills.filter((name) => {
    // Normalize: trim → lowercase → strip trailing ` (...)` group
    const normalized = name.trim().toLowerCase().replace(/\s*\([^)]*\)\s*$/, '');
    if (seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });

  const isNotStated = skillsToolsStatus === 'not_stated' || dedupedSkills.length === 0;

  if (isNotStated) {
    return (
      <SectionCard
        title="Skills & Tools"
        subtitle="Technical skills and software proficiency"
      >
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
          <p className="text-sm text-gray-500">
            No skills captured — review the candidate's resume for technical skills and tools.
          </p>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Skills & Tools"
      subtitle="Technical skills and software proficiency"
    >
      <div className="flex flex-wrap gap-2">
        {dedupedSkills.map((name, index) => (
          <span
            key={`${name}-${index}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg border border-gray-200"
          >
            {name}
          </span>
        ))}
      </div>
    </SectionCard>
  );
};

const RefereesSection = ({ extractedData }) => {
  const referees = extractedData?.referees;
  const refereesStatus = extractedData?.referees_status;

  const hasReferees = Array.isArray(referees) && referees.length > 0;
  const isNotStated = refereesStatus === 'not_stated' || !hasReferees;

  if (isNotStated) {
    return (
      <SectionCard
        title="Referees"
        subtitle="Professional references"
      >
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
          <p className="text-sm text-gray-500">
            Referees not stated — most candidates do not provide referee details in their CV.
          </p>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Referees"
      subtitle="Professional references"
    >
      <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 overflow-hidden">
        {referees.map((referee, index) => {
          const name = typeof referee === 'string' ? referee : referee.name;
          const title = typeof referee === 'object' ? referee.title || referee.position : null;
          const company = typeof referee === 'object' ? referee.company : null;
          const phone = typeof referee === 'object' ? referee.phone : null;
          const email = typeof referee === 'object' ? referee.email : null;

          return (
            <li
              key={`${name}-${index}`}
              className="p-4"
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0"
                  aria-hidden="true"
                >
                  <User className="w-4 h-4 text-gray-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {name || '(Referee name not specified)'}
                  </h4>
                  {title ? (
                    <p className="text-sm text-gray-600 mt-0.5">{title}</p>
                  ) : null}
                  {company ? (
                    <p className="text-sm text-gray-500 mt-0.5">{company}</p>
                  ) : null}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                    {phone ? (
                      <a href={`tel:${phone}`} className="text-xs text-blue-600 hover:underline">
                        {phone}
                      </a>
                    ) : null}
                    {email ? (
                      <a href={`mailto:${email}`} className="text-xs text-blue-600 hover:underline">
                        {email}
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
};

// ============================================================================
// JOB DESCRIPTION PARSER (MCF Markdown)
// ============================================================================

/**
 * Parse MCF job description markdown into structured sections.
 * MCF dumps typically include:
 * - MCF ID / URL
 * - Employment Type
 * - Salary Range
 * - Location
 * - Responsibilities/Requirements as markdown lists
 * 
 * Returns: { metadata, responsibilities, requirementsOrMustHaves, mcfUrl, unconsumedContent }
 * 
 * Examples (from docstring, not automated tests):
 * 
 * 1. Responsibilities then requirements with continuation lines:
 *    ## Responsibilities
 *    - Develop BIM models
 *      and coordinate with teams
 *    - ManageProject timelines
 *    ## Requirements
 *    - Degree in Architecture
 *      or related field
 *    - ProficientIn Revit
 * 
 *    Result: 2 responsibilities, 2 requirements, no cross-contamination
 * 
 * 2. Blank-separated bullets within same section:
 *    ## Requirements
 *    - Degree
 *    
 *    - Revit
 * 
 *    Result: Both bullets in Requirements list (blank does not break section)
 * 
 * 3. Blank after section header:
 *    ## Responsibilities
 *    - Build models
 *    
 *    ## Requirements
 *    
 *    - Degree
 * 
 *    Result: Build models in Responsibilities, Degree in Requirements (blank after header OK)
 * 
 * 4. Trailing prose after bullets:
 *    ## Requirements
 *    - Degree
 *    
 *    Closing note for applicants
 * 
 *    Result: Degree in Requirements, "Closing note" in unconsumed (not appended to bullet)
 * 
 * 4b. Multiline trailing prose after bullets:
 *    ## Requirements
 *    - Degree
 *    
 *    Closing note line one
 *    Closing note line two
 * 
 *    Result: Degree in Requirements, both prose lines in unconsumed (neither appended to bullet)
 * 
 * 4c. Intro prose under second section:
 *    ## Responsibilities
 *    - Build models
 *    ## Requirements
 *    Please review carefully:
 *    - Degree in Architecture
 * 
 *    Result: Build models in Responsibilities, Degree in Requirements, intro prose in unconsumed
 *    (section not cleared because no blank preceded intro)
 * 
 * 5. Generic markdown fallback (no MCF structure):
 *    "This is a general role.\n\n**Apply now.**"
 *    Result: hasStructure=false → ConsentMarkdown renders
 * 
 * 6. URL-only MCF text:
 *    "https://www.mycareersfuture.gov.sg/job/12345"
 *    Result: hasStructure=true, mcfUrl extracted, tertiary link rendered
 * 
 * 7. Inline URL with surrounding prose:
 *    "Apply at https://mycareersfuture.gov.sg/job/123 today"
 *    Result: mcfUrl extracted, "Apply at  today" preserved in unconsumed
 * 
 * 8. Metadata as list items:
 *    - **Salary:** SGD 4000 - 5000
 *    - **Employment Type:** Full-time
 *    - **Location:** Singapore
 *    - **Experience Level:** Mid-level
 * 
 *    Result: All metadata extracted and shown in DetailFieldGrid
 * 
 * 9. Heading containing "responsibilities":
 *    ### Job description / responsibilities
 *    - Build models
 * 
 *    Result: Bullet classified as responsibilities (heading contains keyword)
 */
const parseMCFJobDescription = (markdown) => {
  if (!markdown || typeof markdown !== 'string') {
    return { 
      metadata: {}, 
      responsibilities: [], 
      requirementsOrMustHaves: [], 
      mcfUrl: null,
      unconsumedContent: []
    };
  }

  const lines = markdown.split('\n');
  const metadata = {};
  const responsibilities = [];
  const requirementsOrMustHaves = [];
  const unconsumedContent = [];
  let mcfUrl = null;
  let currentSection = null;
  let lastLineWasBlank = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    let consumed = false;
    
    // Preserve blank lines for paragraph spacing in unconsumed content
    if (!trimmed) {
      unconsumedContent.push('');
      lastLineWasBlank = true;
      // DO NOT reset currentSection here - blank lines are valid within sections
      // (e.g., blank after ## Requirements, blank between bullets)
      // lastLineWasBlank flag prevents prose after blank from appending to bullets
      continue;
    }

    // Extract MCF URL - match any https URL, then check hostname
    // Keep surrounding prose on the line (only extract URL, don't consume whole line)
    const urlMatch = trimmed.match(/https?:\/\/[^\s<]+/i);
    if (urlMatch) {
      const url = urlMatch[0];
      if (url.toLowerCase().includes('mycareersfuture.gov.sg')) {
        mcfUrl = url;
        // Don't mark as fully consumed - preserve the line with URL text removed or keep full line
        const lineWithoutUrl = line.replace(url, '').trim();
        if (lineWithoutUrl) {
          unconsumedContent.push(lineWithoutUrl);
        }
        consumed = true;
        lastLineWasBlank = false;
        continue;
      }
    }

    // Extract MCF ID
    if (!consumed && (trimmed.match(/^MCF ID:?\s*/i) || trimmed.match(/^Job ID:?\s*/i))) {
      consumed = true;
      lastLineWasBlank = false;
    }

    // Extract Employment Type (bare line or list-item, colon inside or outside bold)
    if (!consumed && (
      trimmed.match(/^Employment Type:?\s*/i) || 
      trimmed.match(/^\*\*Employment Type:?\*\*/i) ||
      trimmed.match(/^[-*]\s+\*\*Employment Type:?\*\*/i)
    )) {
      metadata.employmentType = trimmed
        .replace(/^[-*]\s+/, '')  // Remove bullet
        .replace(/^\*\*Employment Type:?\*\*/i, '')
        .replace(/^Employment Type:?\s*/i, '')
        .trim();
      consumed = true;
      lastLineWasBlank = false;
    }

    // Extract Salary Range (bare line or list-item, colon inside or outside bold)
    if (!consumed && (
      trimmed.match(/^Salary:?\s*/i) || 
      trimmed.match(/^\*\*Salary:?\*\*/i) || 
      trimmed.match(/^Salary Range:?\s*/i) ||
      trimmed.match(/^[-*]\s+\*\*Salary( Range)?:?\*\*/i)
    )) {
      metadata.salary = trimmed
        .replace(/^[-*]\s+/, '')  // Remove bullet
        .replace(/^\*\*Salary( Range)?:?\*\*/i, '')
        .replace(/^Salary( Range)?:?\s*/i, '')
        .trim();
      consumed = true;
      lastLineWasBlank = false;
    }

    // Extract Location (bare line or list-item, colon inside or outside bold)
    if (!consumed && (
      trimmed.match(/^(Work )?Location:?\s*/i) || 
      trimmed.match(/^\*\*(Work )?Location:?\*\*/i) ||
      trimmed.match(/^[-*]\s+\*\*(Work )?Location:?\*\*/i)
    )) {
      metadata.location = trimmed
        .replace(/^[-*]\s+/, '')  // Remove bullet
        .replace(/^\*\*(Work )?Location:?\*\*/i, '')
        .replace(/^(Work )?Location:?\s*/i, '')
        .trim();
      consumed = true;
      lastLineWasBlank = false;
    }

    // Extract Experience Level (bare line or list-item, colon inside or outside bold)
    if (!consumed && (
      trimmed.match(/^(Experience )?Level:?\s*/i) || 
      trimmed.match(/^Seniority:?\s*/i) ||
      trimmed.match(/^\*\*(Experience )?Level:?\*\*/i) ||
      trimmed.match(/^\*\*Seniority:?\*\*/i) ||
      trimmed.match(/^[-*]\s+\*\*(Experience )?Level:?\*\*/i) ||
      trimmed.match(/^[-*]\s+\*\*Seniority:?\*\*/i)
    )) {
      metadata.level = trimmed
        .replace(/^[-*]\s+/, '')  // Remove bullet
        .replace(/^\*\*(Experience )?Level:?\*\*/i, '')
        .replace(/^\*\*Seniority:?\*\*/i, '')
        .replace(/^(Experience )?Level:?\s*/i, '')
        .replace(/^Seniority:?\s*/i, '')
        .trim();
      consumed = true;
      lastLineWasBlank = false;
    }

    // Section headers (markdown headings or bold labels)
    if (!consumed && trimmed.match(/^#+\s+/)) {
      // Match headings containing responsibilities/duties keywords (e.g., "### Job description / responsibilities")
      if (trimmed.match(/^#+\s+.*(Responsibilities|Duties|Job description)/i)) {
        currentSection = 'responsibilities';
        consumed = true;
        lastLineWasBlank = false;
      } else if (trimmed.match(/^#+\s+.*(Requirements|Qualifications|Must[- ]Haves?|Must Have)/i)) {
        currentSection = 'requirements';
        consumed = true;
        lastLineWasBlank = false;
      } else {
        // Unknown section heading (e.g., ## Benefits, ## About Us)
        // Preserve in unconsumed and reset section to prevent misclassification
        currentSection = null;
        unconsumedContent.push(line);
        consumed = true;
        lastLineWasBlank = false;
      }
    }

    // Bold section labels (e.g., **Job Responsibilities:**, **Requirements:**)
    // Treat as section headers that set currentSection (don't trigger wasAfterBlank clear)
    if (!consumed && trimmed.match(/^\*\*(Job\s+)?(Responsibilities|Duties|Requirements|Must[- ]Haves?):?\*\*/i)) {
      if (trimmed.match(/^\*\*(Job\s+)?(Responsibilities|Duties):?\*\*/i)) {
        currentSection = 'responsibilities';
      } else if (trimmed.match(/^\*\*(Requirements|Must[- ]Haves?):?\*\*/i)) {
        currentSection = 'requirements';
      }
      consumed = true;
      lastLineWasBlank = false;
    }

    // Bullet points (hyphen, asterisk, or numbered)
    if (!consumed) {
      const bulletMatch = trimmed.match(/^[-*]\s+(.+)$/) || trimmed.match(/^\d+\.\s+(.+)$/);
      if (bulletMatch) {
        const content = fixGluedWords(bulletMatch[1]);
        if (currentSection === 'responsibilities') {
          responsibilities.push(content);
          consumed = true;
          lastLineWasBlank = false;
        } else if (currentSection === 'requirements') {
          requirementsOrMustHaves.push(content);
          consumed = true;
          lastLineWasBlank = false;
        } else if (currentSection === null) {
          // Bullet under unknown section or no section - preserve in unconsumed
          unconsumedContent.push(line);
          consumed = true;
          lastLineWasBlank = false;
        } else {
          // No section header yet, guess based on keywords (only if no unknown section)
          const lower = content.toLowerCase();
          if (lower.includes('responsible') || lower.includes('manage') || lower.includes('coordinate') || lower.includes('develop')) {
            responsibilities.push(content);
            consumed = true;
            lastLineWasBlank = false;
          } else if (lower.includes('degree') || lower.includes('experience') || lower.includes('knowledge') || lower.includes('proficient')) {
            requirementsOrMustHaves.push(content);
            consumed = true;
            lastLineWasBlank = false;
          } else {
            responsibilities.push(content);  // Default to responsibilities
            consumed = true;
            lastLineWasBlank = false;
          }
        }
      }
    }

    // Continuation lines (belong to current bullet)
    // Only append if we haven't seen a blank line since the last bullet
    if (!consumed && !lastLineWasBlank) {
      if (currentSection === 'responsibilities' && responsibilities.length > 0 && !trimmed.match(/^[#*-]/)) {
        responsibilities[responsibilities.length - 1] += ' ' + fixGluedWords(trimmed);
        consumed = true;
      } else if (currentSection === 'requirements' && requirementsOrMustHaves.length > 0 && !trimmed.match(/^[#*-]/)) {
        requirementsOrMustHaves[requirementsOrMustHaves.length - 1] += ' ' + fixGluedWords(trimmed);
        consumed = true;
      }
    }

    // Track unconsumed content (prose, intro, unrecognized sections)
    if (!consumed && trimmed) {
      unconsumedContent.push(line);
      // If this is ordinary prose after a blank (trailing prose after list), clear section
      // Only clear if: (1) we saw a blank before this line, AND (2) current section has bullets
      const wasAfterBlank = lastLineWasBlank;
      lastLineWasBlank = false;
      
      if (wasAfterBlank && currentSection !== null) {
        // Check if CURRENT section (not just any section) has items
        const currentSectionHasItems = 
          (currentSection === 'responsibilities' && responsibilities.length > 0) ||
          (currentSection === 'requirements' && requirementsOrMustHaves.length > 0);
        
        if (currentSectionHasItems) {
          // This is trailing prose after bullets in this section - clear to prevent appending
          currentSection = null;
        }
      }
    }
  }

  return { 
    metadata, 
    responsibilities, 
    requirementsOrMustHaves, 
    mcfUrl,
    unconsumedContent
  };
};

/**
 * Fix glued words ONLY in camelCase/PascalCase text.
 * Adds space before uppercase letters when preceded by lowercase.
 * Examples: "DevelopBIM" -> "Develop BIM", "coordinateWith" -> "coordinate With"
 * 
 * DOES NOT fix all-lowercase glued text (e.g., "dvelopaccurate" stays unchanged).
 * All-lowercase corruption requires dictionary/heuristic approaches outside parser scope.
 */
const fixGluedWords = (text) => {
  // Add space before capital letter if preceded by lowercase (camelCase boundary only)
  return text.replace(/([a-z])([A-Z])/g, '$1 $2');
};

/**
 * Render parsed MCF job description with proper structure.
 */
const JobDescriptionContent = ({ job }) => {
  if (!job.job_description || job.job_description.trim() === '') {
    return (
      <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
        <p className="text-sm text-gray-500">
          No job description provided.
        </p>
      </div>
    );
  }

  const parsed = parseMCFJobDescription(job.job_description);
  const { metadata, responsibilities, requirementsOrMustHaves, mcfUrl, unconsumedContent } = parsed;

  // If nothing was parsed (no structure), fall back to ConsentMarkdown
  // Include mcfUrl in structure check so URL-only MCF text still shows the tertiary link
  const hasStructure = 
    Object.keys(metadata).length > 0 || 
    responsibilities.length > 0 || 
    requirementsOrMustHaves.length > 0 ||
    mcfUrl !== null;

  if (!hasStructure) {
    return <ConsentMarkdown markdown={job.job_description} />;
  }

  return (
    <div className="space-y-4">
      {/* Unconsumed Content (intro, summary, unrecognized prose) */}
      {unconsumedContent.length > 0 && (
        <ConsentMarkdown markdown={unconsumedContent.join('\n')} />
      )}

      {/* Metadata Grid */}
      {Object.keys(metadata).length > 0 && (
        <DetailFieldGrid>
          {metadata.employmentType && (
            <DetailField
              label="Employment Type"
              value={metadata.employmentType}
              className="col-span-2 sm:col-span-1"
            />
          )}
          {metadata.salary && (
            <DetailField
              label="Salary Range"
              value={metadata.salary}
              className="col-span-2 sm:col-span-1"
            />
          )}
          {metadata.location && (
            <DetailField
              label="Location"
              value={metadata.location}
              className="col-span-2 sm:col-span-1"
            />
          )}
          {metadata.level && (
            <DetailField
              label="Experience Level"
              value={metadata.level}
              className="col-span-2 sm:col-span-1"
            />
          )}
        </DetailFieldGrid>
      )}

      {/* Responsibilities */}
      {responsibilities.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Responsibilities</h4>
          <ul className="list-disc list-outside pl-5 space-y-1.5">
            {responsibilities.map((item, index) => (
              <li key={index} className="text-sm text-gray-700 leading-relaxed">
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Requirements */}
      {requirementsOrMustHaves.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Requirements</h4>
          <ul className="list-disc list-outside pl-5 space-y-1.5">
            {requirementsOrMustHaves.map((item, index) => (
              <li key={index} className="text-sm text-gray-700 leading-relaxed">
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* MCF Link (tertiary) */}
      {mcfUrl && (
        <div className="pt-2 border-t border-gray-100">
          <a
            href={mcfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-500 hover:text-gray-700 underline inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" />
            View on MyCareersFuture
          </a>
        </div>
      )}
    </div>
  );
};

const JDToolsMatchSection = ({ extractedData }) => {
  const jdTools = extractedData?.jd_tools;

  if (!jdTools || typeof jdTools !== 'object') {
    return (
      <SectionCard
        title="JD Tools Match"
        subtitle="Candidate skills vs job requirements"
      >
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
          <p className="text-sm text-gray-500">
            JD tools not computed — screening analysis not yet available.
          </p>
        </div>
      </SectionCard>
    );
  }

  const rawHits = Array.isArray(jdTools.hit) ? jdTools.hit : [];
  const rawMisses = Array.isArray(jdTools.miss) ? jdTools.miss : [];

  // Split composite tools and strip parenthetical annotations
  // Strategy:
  // 1. Split on commas and semicolons first (primary bundle separators)
  // 2. Then split on ' / ' (space-slash-space) for explicit lists
  // 3. Bare '/' (no spaces) ONLY when BOTH sides are multi-letter tokens
  //    (Excel/PowerPoint OK, but never shatter R/3, S/4, OS/2)
  const splitAndCleanTools = (toolList) => {
    const cleaned = [];
    toolList.forEach((tool) => {
      if (typeof tool !== 'string') return;
      
      // First pass: split on comma and semicolon (bundle separators)
      const commaParts = tool.split(/[,;]/).map((s) => s.trim()).filter((s) => s.length > 0);
      
      commaParts.forEach((part) => {
        // Second pass: split on ' / ' (space-slash-space)
        const spacedSlashParts = part.split(/\s+\/\s+/).map((s) => s.trim()).filter((s) => s.length > 0);
        
        spacedSlashParts.forEach((segment) => {
          // Third pass: bare '/' without spaces, but ONLY if both sides are multi-letter
          // This preserves R/3, S/4, OS/2 while splitting Excel/PowerPoint
          const bareSlashParts = segment.split('/');
          
          if (bareSlashParts.length > 1) {
            // Check if all parts look like separate tools (multi-letter tokens)
            const allMultiLetter = bareSlashParts.every((token) => {
              const trimmed = token.trim();
              return trimmed.length > 1 && /^[A-Za-z]/.test(trimmed);
            });
            
            if (allMultiLetter) {
              // Safe to split: Excel/PowerPoint/JavaScript
              bareSlashParts.forEach((token) => {
                const trimmed = token.trim();
                // Strip parenthetical annotations like (IT), (nice), (must)
                const cleanedToken = trimmed.replace(/\s*\([^)]*\)\s*$/g, '').trim();
                if (cleanedToken.length > 0) {
                  cleaned.push(cleanedToken);
                }
              });
            } else {
              // Contains version fragments (R/3, S/4) - keep whole segment
              const cleanedSegment = segment.replace(/\s*\([^)]*\)\s*$/g, '').trim();
              if (cleanedSegment.length > 0) {
                cleaned.push(cleanedSegment);
              }
            }
          } else {
            // No bare slash - keep as-is
            const cleanedSegment = segment.replace(/\s*\([^)]*\)\s*$/g, '').trim();
            if (cleanedSegment.length > 0) {
              cleaned.push(cleanedSegment);
            }
          }
        });
      });
    });
    return cleaned;
  };

  // Defensive denylist: filter out known non-software tokens (process/deliverable phrases)
  // that may have leaked into server's jd_tools.hit/miss arrays
  const NON_SOFTWARE_DENYLIST = [
    // Process/deliverable phrases
    'shop drawings', 'shop drawing', 'as-built', 'as-builts', 'as built', 'as builts',
    'coordination drawings', 'coordination drawing', 'working drawings', 'working drawing',
    'construction drawings', 'construction drawing', 'fabrication drawings', 'fabrication drawing',
    'installation drawings', 'installation drawing',
    
    // Discipline/domain terms
    'mep', 'mep coordination', 'structural', 'architectural', 'civil', 'mechanical',
    'electrical', 'plumbing', 'hvac', 'fire protection', 'structural engineering',
    
    // Document types
    'boq', 'bill of quantities', 'tender', 'tender documents', 'specifications',
    'rfp', 'request for proposal', 'submittals', 'submittal',
    
    // Design/project phase terms
    'design development', 'schematic design', 'concept design', 'detailed design',
    'construction documentation', 'site coordination', 'project coordination',
    
    // Generic skill categories
    'communication', 'teamwork', 'problem solving', 'leadership', 'time management',
    'project management', 'site management', 'quality control', 'quality assurance',
    
    // Certifications/qualifications (not software)
    'degree', 'diploma', 'certification', 'licensed', 'registration', 'professional engineer',
    
    // FSSD and variants
    'fssd', 'fire safety', 'fire safety & shelter', 'fire safety and shelter',
    'shelter', 'shelter design',
  ];
  
  const isNonSoftware = (token) => {
    const lower = token.toLowerCase().trim();
    
    // Check each banned phrase/token
    return NON_SOFTWARE_DENYLIST.some((banned) => {
      const bannedLower = banned.toLowerCase();
      
      // Exact match (fast path)
      if (lower === bannedLower) return true;
      
      // Multi-word phrase: check if phrase appears with word boundaries
      // (e.g., "shop drawings" should match "shop drawings" or "MEP shop drawings" but not "shopsdrawings")
      if (bannedLower.includes(' ')) {
        const phraseRegex = new RegExp(`\\b${bannedLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        return phraseRegex.test(token);
      }
      
      // Single-word token: whole-word match only
      // (e.g., "tender" matches "Tender" or "MEP, tender" but NOT "TenderLink")
      const wordRegex = new RegExp(`\\b${bannedLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      return wordRegex.test(token);
    });
  };
  
  const filterSoftwareOnly = (tools) => tools.filter((tool) => !isNonSoftware(tool));

  const hits = filterSoftwareOnly(splitAndCleanTools(rawHits));
  const misses = filterSoftwareOnly(splitAndCleanTools(rawMisses));

  if (hits.length === 0 && misses.length === 0) {
    return (
      <SectionCard
        title="JD Tools Match"
        subtitle="Candidate skills vs job requirements"
      >
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
          <p className="text-sm text-gray-500">
            No tools to match — job requirements or candidate skills not yet captured.
          </p>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="JD Tools Match"
      subtitle="Candidate skills vs job requirements"
    >
      <div className="space-y-3">
        {hits.length > 0 ? (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Match
              </span>
              <CountBadge count={hits.length} theme="success" size="sm" />
            </div>
            <div className="flex flex-wrap gap-2">
              {hits.map((tool, index) => (
                <span
                  key={`hit-${tool}-${index}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-800 bg-green-50 rounded-lg border border-green-200"
                >
                  {tool}
                </span>
              ))}
            </div>
          </div>
        ) : null}
        {misses.length > 0 ? (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Missing
              </span>
              <CountBadge count={misses.length} theme="warning" size="sm" />
            </div>
            <div className="flex flex-wrap gap-2">
              {misses.map((tool, index) => (
                <span
                  key={`miss-${tool}-${index}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-800 bg-amber-50 rounded-lg border border-amber-200"
                >
                  {tool}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
};

const AlsoConsideredSection = ({ otherApplications, onSelect }) => {
  if (!otherApplications.length) return null;

  return (
    <SectionCard
      title="Also considered for"
      subtitle="Same person, separate applications. Stage, review, and offer stay independent."
    >
      <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 overflow-hidden">
        {otherApplications.map((other) => {
          const jobTitle = other.job_title || other.job?.job_title || '—';
          const departmentName = other.department_name || other.job?.department_name || '';
          const stage = other.current_state_value ?? other.current_state_obj?.state ?? 'new';
          return (
            <li key={other.id}>
              <button
                type="button"
                onClick={() => onSelect(other.id)}
                className="w-full min-h-11 px-4 py-3 flex items-center gap-3 text-left hover:bg-red-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-inset"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate" title={jobTitle}>
                    {jobTitle}
                  </p>
                  {departmentName ? (
                    <div className="mt-1">
                      <InfoBadge
                        color={getDepartmentBadgeColor(departmentName)}
                        label={getDepartmentShortName(departmentName)}
                        title={departmentName}
                      />
                    </div>
                  ) : null}
                </div>
                <StatusBadge status={stage} />
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
};

const ProfileSection = ({ application, otherApplications = [], onSelectOther }) => {
  const candidate = application?.candidate;
  const currentState = application?.current_state_value ?? application?.current_state_obj?.state ?? 'new';
  const trackerNextAction = application?.tracker_next_action;
  const nextAction = trackerNextAction || NEXT_ACTIONS[currentState];
  const driveResumeLink = application?.drive_resume_link
    || candidate?.extracted_data?.drive_resume_link;
  
  if (!candidate) return null;
  
  return (
    <div className="space-y-4">
      <SectionCard title="Next Action" subtitle="Suggested next step for this application">
        <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-900">{nextAction || '—'}</p>
        </div>
      </SectionCard>

      <SectionCard title="CV Summary" subtitle="Contact details and how they applied">
        <DetailFieldGrid>
          <DetailField
            label="Personal Email"
            value={displayPersonalEmail(candidate.personal_email)}
            className="col-span-2 sm:col-span-1"
          />
          <DetailField label="Phone Number" value={candidate.personal_phone_number} />
          <DetailField
            label="Source"
            value={candidate.source_display || candidate.source}
          />
        </DetailFieldGrid>
      </SectionCard>

      <WorkHistorySection workHistory={candidate.extracted_data?.work_history} />

      <EducationSection extractedData={candidate.extracted_data} />

      <SalarySection extractedData={candidate.extracted_data} />

      <CertificationsSection extractedData={candidate.extracted_data} />

      <WorkPassSection extractedData={candidate.extracted_data} />

      <NoticeAvailabilitySection extractedData={candidate.extracted_data} />

      <SkillsToolsSection extractedData={candidate.extracted_data} />

      <RefereesSection extractedData={candidate.extracted_data} />

      <JDToolsMatchSection extractedData={candidate.extracted_data} />

      <AlsoConsideredSection
        otherApplications={otherApplications}
        onSelect={onSelectOther}
      />
      
      {(driveResumeLink || candidate.resume_url) && (
        <SectionCard title="Resume" subtitle="Open the candidate CV">
          <div className="flex flex-wrap gap-2">
            {driveResumeLink && (
              <a
                href={driveResumeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-admin-outline flex items-center gap-2 w-fit"
              >
                <ExternalLink className="w-4 h-4" />
                Open Drive CV
              </a>
            )}
            {candidate.resume_url && (
              <a
                href={candidate.resume_url}
                target="_blank"
                rel="noreferrer"
                className="btn btn-admin-outline flex items-center gap-2 w-fit"
              >
                <FileText className="w-4 h-4" />
                View Resume
                <ChevronRight className="w-4 h-4" />
              </a>
            )}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

const JobSection = ({ application }) => {
  const job = application?.job;
  if (!job) return null;
  const hiringManagers = Array.isArray(job.hiring_managers) ? job.hiring_managers : [];
  
  return (
    <div className="space-y-4">
      <SectionCard title="Job Details" subtitle="Position information">
        <DetailFieldGrid>
          <DetailField
            label="Job Title"
            value={job.job_title}
            span="wide"
            valueClassName="font-medium"
          />
          <DetailBadgeField label="Status">
            <StatusBadge
              status={job.status}
              label={job.status_display || job.status}
            />
          </DetailBadgeField>
          <DetailField label="Department" valueClassName="min-w-0">
            {job.department_name ? (
              <>
                <DepartmentIdentityMark name={job.department_name} size="sm" />
                <span className="text-sm text-gray-900 truncate" title={job.department_name}>
                  {job.department_name}
                </span>
              </>
            ) : (
              '—'
            )}
          </DetailField>
          <DetailField
            label="Posted on MCF"
            value={job.posted_on_mcf ? 'Yes' : 'No'}
          />
          <DetailField
            label="Hiring managers"
            span="full"
            multiline
            valueClassName="flex-wrap gap-x-4 gap-y-2"
          >
            {hiringManagers.length > 0 ? (
              hiringManagers.map((manager) => (
                <div
                  key={`${manager.role}-${manager.staff_id}`}
                  className="flex items-center gap-2 min-w-0"
                >
                  <span className="text-sm text-gray-900 truncate">{manager.name}</span>
                  <RoleBadge
                    employee={{
                      is_hod: manager.role === 'hod',
                      is_deputy_hod: manager.role === 'deputy_hod',
                      hod_departments: manager.role === 'hod' ? [job.department_name] : [],
                      deputy_hod_departments: manager.role === 'deputy_hod'
                        ? [job.department_name]
                        : [],
                      portal_groups: [],
                    }}
                    size="sm"
                    showPortalGroups={false}
                  />
                </div>
              ))
            ) : (
              <span className="text-sm text-gray-500">
                No HOD or Dy HOD configured for this department
              </span>
            )}
          </DetailField>
        </DetailFieldGrid>
      </SectionCard>
      
      <SectionCard title="Job Description" subtitle="Role requirements and responsibilities">
        <JobDescriptionContent job={job} />
      </SectionCard>
    </div>
  );
};

const ReviewSection = ({ application }) => {
  const reviews = application?.reviews || [];
  
  if (reviews.length === 0) {
    return (
      <div className="bg-gray-50 rounded-2xl border border-gray-200 p-12 text-center">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No reviews yet</h3>
        <p className="text-sm text-gray-500">
          Hiring manager reviews will appear here once submitted.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {reviews.map((review, index) => {
        const imported = isTrackerImportedReview(review);
        const ratingIsInferred = review.rating_is_inferred ?? imported;
        const noteSections = reviewNoteSections(review.notes);
        const sequence = reviews.length > 1 ? ` #${reviews.length - index}` : '';
        const title = imported ? `Imported assessment${sequence}` : `Review${sequence}`;
        const subtitle = imported
          ? `Imported from ${review.source_display || 'Applicant Tracker'} on ${formatDateTime(review.created_at)}`
          : `By ${review.reviewer_staff_id} on ${formatDateTime(review.created_at)}`;

        return (
          <SectionCard
            key={review.id}
            title={title}
            subtitle={subtitle}
          >
            <DetailFieldGrid>
              {!ratingIsInferred ? (
                <DetailField
                  label="Rating"
                  value={review.rating_display || review.rating}
                />
              ) : null}
              <DetailBadgeField label={imported ? 'Tracker score' : 'Recommendation'}>
                <ScorecardBadge
                  recommendation={review.recommendation}
                  recommendationDisplay={review.recommendation_display}
                />
              </DetailBadgeField>
              {noteSections.length === 0 ? (
                <DetailField
                  label="Assessment"
                  span="full"
                  value="No assessment notes provided."
                  valueClassName="text-gray-500"
                />
              ) : (
                noteSections.map((section, sectionIndex) => (
                  <DetailField
                    key={`${review.id}-${section.key}-${sectionIndex}`}
                    label={section.label}
                    span="full"
                    multiline
                  >
                    {section.text}
                  </DetailField>
                ))
              )}
            </DetailFieldGrid>
          </SectionCard>
        );
      })}
    </div>
  );
};

const HistorySection = ({ application }) => {
  const history = useMemo(() => {
    const rows = application?.state_history || [];
    return [...rows].sort(
      (left, right) =>
        new Date(left.changed_at || 0) - new Date(right.changed_at || 0)
    );
  }, [application?.state_history]);

  if (history.length === 0) {
    return (
      <div className="bg-gray-50 rounded-2xl border border-gray-200 p-12 text-center">
        <HistoryIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No history yet</h3>
        <p className="text-sm text-gray-500">
          Stage changes will appear here as the application moves through hiring.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        className="absolute left-4 top-1 bottom-1 w-px bg-gray-200"
        aria-hidden="true"
      />
      <ol className="space-y-6">
        {history.map((state) => (
          <li key={state.id} className="relative pl-12">
            <div
              className={`absolute left-2 top-1.5 w-4 h-4 rounded-full border-2 border-white shadow ${
                state.is_current ? 'bg-red-500' : 'bg-gray-300'
              }`}
              aria-hidden="true"
            />
            <p className="text-xs text-gray-500">
              {formatDateTime(state.changed_at)}
              {state.changed_by_staff_id ? ` · ${state.changed_by_staff_id}` : ''}
            </p>
            <div className="mt-1">
              <StatusBadge
                status={state.state}
                label={state.state_display || state.state}
              />
            </div>
            {state.reason ? (
              <p className="text-sm text-gray-700 mt-1.5">{state.reason}</p>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT — RecruitmentBoard
// ============================================================================

const RecruitmentBoard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isCompact = useIsMobile(1024);
  
  const urlId = searchParams.get('id');
  const [selectedId, setSelectedId] = useState(urlId ? parseInt(urlId, 10) : null);
  const [activeSection, setActiveSection] = useState(searchParams.get('section') || 'profile');
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState(() => {
    const fromUrl = searchParams.get('state');
    return isKnownState(fromUrl) ? fromUrl : 'all';
  });
  const [departmentFilter, setDepartmentFilter] = useState(
    () => searchParams.get('department') || 'all'
  );
  const [parkReasonFilter, setParkReasonFilter] = useState(
    () => searchParams.get('park_reason') || 'all'
  );
  
  const { data: applications = [], isLoading: listLoading } = useApplicationsList({
    page_size: 1000,
  });
  
  const { data: application, isLoading: detailLoading } = useApplicationDetail(selectedId);

  const otherApplicationsById = useMemo(() => {
    const grouped = new Map();
    applications.forEach((app) => {
      const key = candidateKey(app);
      if (key == null) return;
      const bucketKey = String(key);
      if (!grouped.has(bucketKey)) grouped.set(bucketKey, []);
      grouped.get(bucketKey).push(app);
    });
    const byId = new Map();
    grouped.forEach((apps) => {
      if (apps.length < 2) return;
      apps.forEach((app) => {
        byId.set(app.id, apps.filter((other) => other.id !== app.id));
      });
    });
    return byId;
  }, [applications]);
  
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (selectedId) next.set('id', selectedId.toString());
    else next.delete('id');
    if (activeSection && activeSection !== 'profile') next.set('section', activeSection);
    else next.delete('section');
    if (stateFilter && stateFilter !== 'all') next.set('state', stateFilter);
    else next.delete('state');
    if (departmentFilter && departmentFilter !== 'all') next.set('department', departmentFilter);
    else next.delete('department');
    if (parkReasonFilter && parkReasonFilter !== 'all') next.set('park_reason', parkReasonFilter);
    else next.delete('park_reason');
    setSearchParams(next, { replace: true });
  }, [selectedId, activeSection, stateFilter, departmentFilter, parkReasonFilter, searchParams, setSearchParams]);
  
  useEffect(() => {
    if (stateFilter !== 'kiv' && parkReasonFilter !== 'all') {
      setParkReasonFilter('all');
    }
  }, [stateFilter, parkReasonFilter]);

  const departmentFilterOptions = useMemo(() => {
    const byId = new Map();
    applications.forEach((app) => {
      const key = departmentKey(app);
      if (!byId.has(key)) {
        byId.set(key, { label: departmentLabel(app), count: 0 });
      }
      byId.get(key).count += 1;
    });
    const departments = Array.from(byId.entries()).sort((a, b) => {
      if (a[0] === UNASSIGNED_DEPARTMENT) return 1;
      if (b[0] === UNASSIGNED_DEPARTMENT) return -1;
      return a[1].label.localeCompare(b[1].label);
    }).map(([value, { label, count }]) => ({
      value,
      label: `${label} (${count})`,
    }));
    return [{ value: 'all', label: 'All departments' }, ...departments];
  }, [applications]);

  const showDepartmentFilter = departmentFilterOptions.length > 2;

  useEffect(() => {
    if (!applications.length || departmentFilter === 'all') return;
    const known = departmentFilterOptions.some((option) => option.value === departmentFilter);
    if (!known) setDepartmentFilter('all');
  }, [applications.length, departmentFilter, departmentFilterOptions]);

  const departmentScopedApplications = useMemo(
    () => applications.filter((app) => matchesDepartmentFilter(app, departmentFilter)),
    [applications, departmentFilter]
  );

  const stateCounts = useMemo(() => {
    const counts = { all: departmentScopedApplications.length };
    STATE_FILTERS.forEach((filter) => {
      if (filter.value !== 'all') {
        counts[filter.value] = departmentScopedApplications.filter(
          (app) => (app.current_state_value ?? app.current_state_obj?.state) === filter.value
        ).length;
      }
    });
    return counts;
  }, [departmentScopedApplications]);
  
  const filteredApplications = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return departmentScopedApplications.filter((app) => {
      const appState = app.current_state_value ?? app.current_state_obj?.state;
      if (stateFilter !== 'all' && appState !== stateFilter) {
        return false;
      }
      if (parkReasonFilter !== 'all' && app.park_reason !== parkReasonFilter) {
        return false;
      }
      if (!term) return true;
      return (
        (app.candidate_name || '').toLowerCase().includes(term) ||
        (app.job_title || '').toLowerCase().includes(term) ||
        displayPersonalEmail(app.candidate_email).toLowerCase().includes(term)
      );
    });
  }, [departmentScopedApplications, searchTerm, stateFilter, parkReasonFilter]);

  const groupedApplications = useMemo(() => {
    const groups = new Map();
    filteredApplications.forEach((app) => {
      const key = departmentKey(app);
      if (!groups.has(key)) {
        groups.set(key, { id: key, name: departmentLabel(app), applications: [] });
      }
      groups.get(key).applications.push(app);
    });
    const ordered = Array.from(groups.values()).sort((a, b) => {
      if (a.id === UNASSIGNED_DEPARTMENT) return 1;
      if (b.id === UNASSIGNED_DEPARTMENT) return -1;
      return a.name.localeCompare(b.name);
    });
    ordered.forEach((group) => {
      group.applications.sort((a, b) =>
        (a.candidate_name || '').localeCompare(b.candidate_name || '')
      );
    });
    return ordered;
  }, [filteredApplications]);

  const showDepartmentGroups = groupedApplications.length > 1;

  const selectApplication = (applicationId) => {
    const target = applications.find((app) => app.id === applicationId);
    if (target) {
      const targetState = target.current_state_value ?? target.current_state_obj?.state;
      if (stateFilter !== 'all' && targetState !== stateFilter) {
        setStateFilter('all');
      }
      if (departmentFilter !== 'all' && !matchesDepartmentFilter(target, departmentFilter)) {
        setDepartmentFilter('all');
      }
    }
    setSelectedId(applicationId);
  };

  const selectedOtherApplications = useMemo(() => {
    const fromList = otherApplicationsById.get(selectedId) || [];
    if (fromList.length) return fromList;
    return application?.other_applications || [];
  }, [otherApplicationsById, selectedId, application]);
  
  // On URL deep-link load, widen filters if the selected app is not in the current filtered list
  useEffect(() => {
    if (!applications.length || !selectedId) return;
    
    const stillVisible = filteredApplications.some((app) => app.id === selectedId);
    if (stillVisible) return;
    
    const target = applications.find((app) => app.id === selectedId);
    if (!target) return;
    
    // Apply the same filter-widening logic as selectApplication
    const targetState = target.current_state_value ?? target.current_state_obj?.state;
    if (stateFilter !== 'all' && targetState !== stateFilter) {
      setStateFilter('all');
    }
    if (departmentFilter !== 'all' && !matchesDepartmentFilter(target, departmentFilter)) {
      setDepartmentFilter('all');
    }
  }, [applications.length]);
  
  useEffect(() => {
    if (filteredApplications.length === 0) {
      if (selectedId) setSelectedId(null);
      return;
    }
    const stillVisible = filteredApplications.some((app) => app.id === selectedId);
    if (selectedId && !stillVisible) {
      setSelectedId(isCompact ? null : filteredApplications[0].id);
      return;
    }
    if (!selectedId && !isCompact) {
      setSelectedId(filteredApplications[0].id);
    }
  }, [selectedId, filteredApplications, isCompact]);

  const showInbox = !isCompact || !selectedId;
  const showDetail = !isCompact || Boolean(selectedId);
  const showHeaderBack = isCompact && Boolean(selectedId);

  const handleBackToApplications = () => {
    setSelectedId(null);
  };
  
  return (
    <div className="bg-gray-50 flex flex-col gap-6 min-w-0 lg:h-[calc(100dvh-7.5rem)] lg:min-h-0 lg:overflow-hidden">
      <div className="flex-shrink-0">
        {showHeaderBack ? (
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleBackToApplications}
              aria-label="Back to applications"
              title="Back to applications"
              className="icon-button-admin flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">Recruitment</h1>
              <p className="text-sm text-gray-500 mt-1">
                Live hiring pipeline. Review applications, move stages, and prepare offers.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <UserCheck className="w-5 h-5 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Recruitment</h1>
            </div>
            <p className="text-sm text-gray-500 ml-12">
              Live hiring pipeline. Review applications, move stages, and prepare offers.
            </p>
          </>
        )}
      </div>

      {/* Closed 13-stage funnel — navigation tabs, not a data-driven pill bar.
          Hide the scrollbar; wrap on desktop. On a phone the strip is inbox
          chrome only, so it leaves with the list when a candidate is open. */}
      {showInbox ? (
        <div
          className="flex-shrink-0 min-w-0 w-full flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth"
          role="group"
          aria-label="Filter applications by recruitment stage"
        >
          {STATE_FILTERS.map((filter) => {
            const isActive = stateFilter === filter.value;
            const count = stateCounts[filter.value] || 0;
            return (
              <button
                key={filter.value}
                type="button"
                onClick={() => setStateFilter(filter.value)}
                className={`tab-btn-admin !flex-shrink-0 ${isActive ? 'active' : ''}`}
                aria-pressed={isActive}
              >
                {filter.label}
                {count > 0 && (
                  <CountBadge count={count} theme="admin" active={isActive} />
                )}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 min-w-0 lg:grid-cols-[340px_minmax(0,1fr)] lg:flex-1 lg:min-h-0 lg:overflow-hidden">
      {showInbox ? (
      <aside className="w-full min-w-0 min-h-[600px] bg-white rounded-2xl shadow-sm border flex flex-col overflow-hidden lg:min-h-0">
        <div className="p-4 border-b flex-shrink-0">
          <div className="mb-3 flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900">Applications</h2>
            <CountBadge count={filteredApplications.length} theme="admin" size="md" />
          </div>
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder="Search candidates or jobs..."
          />
          {showDepartmentFilter && (
            <div className="mt-3">
              <PillSelect
                options={departmentFilterOptions}
                value={departmentFilter}
                onChange={setDepartmentFilter}
                theme="admin"
                icon={Building2}
                iconColor="text-red-600"
                className="w-full"
                ariaLabel="Filter applications by department"
              />
            </div>
          )}
          {stateFilter === 'kiv' && (
            <div className="mt-3">
              <PillSelect
                options={PARK_REASON_FILTERS}
                value={parkReasonFilter}
                onChange={setParkReasonFilter}
                theme="admin"
                icon={Pause}
                iconColor="text-amber-600"
                className="w-full"
                ariaLabel="Filter applications by hold/KIV reason"
              />
            </div>
          )}
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {listLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No applications found</h3>
              <p className="text-sm text-gray-500">
                {searchTerm
                  ? 'Try a different search term.'
                  : departmentFilter !== 'all'
                    ? 'No applications in this department for the selected stage.'
                    : stateFilter !== 'all'
                      ? 'No applications in this stage.'
                      : 'New applications appear here when Hiring adds a candidate.'}
              </p>
            </div>
          ) : (
            groupedApplications.map((group) => (
              <section key={group.id} aria-label={group.name}>
                {showDepartmentGroups && (
                  <DepartmentGroupHeader name={group.name} count={group.applications.length} />
                )}
                <div className="divide-y">
                  {group.applications.map((app) => (
                    <ApplicationListItem
                      key={app.id}
                      application={app}
                      isSelected={app.id === selectedId}
                      onSelect={() => selectApplication(app.id)}
                      showStageBadge={stateFilter === 'all'}
                      otherApplications={otherApplicationsById.get(app.id) || []}
                    />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      </aside>
      ) : null}
      
      {showDetail ? (
      <div className="min-w-0 min-h-[600px] flex flex-col lg:min-h-0">
      <main className="flex-1 min-h-0 bg-white rounded-2xl shadow-sm border overflow-hidden flex flex-col">
        {!selectedId ? (
          <div className="flex-1 flex items-center justify-center text-center p-12">
            <div>
              <UserCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No application selected</h3>
              <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                Select an application from the list to review their profile, job, and scorecard.
              </p>
            </div>
          </div>
        ) : detailLoading || !application ? (
          <div className="flex-1 flex items-center justify-center">
            <LoadingSpinner message="Loading application..." />
          </div>
        ) : (
          <>
            <CandidateHeader
              application={application}
              otherApplications={selectedOtherApplications}
            />
            <SectionTabs
              sections={SECTIONS}
              active={activeSection}
              onChange={setActiveSection}
            />
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              {activeSection === 'profile' && (
                <ProfileSection
                  application={application}
                  otherApplications={selectedOtherApplications}
                  onSelectOther={selectApplication}
                />
              )}
              {activeSection === 'job' && <JobSection application={application} />}
              {activeSection === 'review' && <ReviewSection application={application} />}
              {activeSection === 'history' && <HistorySection application={application} />}
            </div>
          </>
        )}
      </main>
      </div>
      ) : null}
      </div>
    </div>
  );
};

export default RecruitmentBoard;
